import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiPlay, FiPause, FiStar, FiHeart, FiGlobe, FiBookOpen, FiUsers, FiArrowRight } = FiIcons;

function App() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const audioRef = useRef(null);

  const lessons = [
    {
      amharic: "እንዴት ነህ?",
      english: "How are you? (to a male)",
      pronunciation: "Endet neh?",
      audioUrl: "https://www.bkamharic.com/new-year/audio/endet-neh.mp3"
    },
    {
      amharic: "እንዴት ነሽ?", 
      english: "How are you? (to a female)",
      pronunciation: "Endet nesh?",
      audioUrl: "https://www.bkamharic.com/new-year/audio/endet-nesh.mp3"
    },
    {
      amharic: "ደህና ነኝ",
      english: "I am fine",
      pronunciation: "Dehna negn",
      audioUrl: "https://www.bkamharic.com/new-year/audio/dehna-negn.mp3"
    },
    {
      amharic: "አመሰግናለሁ",
      english: "Thank you", 
      pronunciation: "Amesegnalewu",
      audioUrl: "https://www.bkamharic.com/new-year/audio/amesegnalewu.mp3"
    }
  ];

  const toggleAudio = (url) => {
    if (audioRef.current) {
      if (isPlaying && audioRef.current.src === url) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.src = url;
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const playGreeting = () => {
    toggleAudio("https://www.bkamharic.com/new-year/audio/melkam-addis-amet.mp3");
  };

  const nextLesson = () => {
    setCurrentLesson((prev) => (prev + 1) % lessons.length);
    setShowAnswer(false);
    setIsPlaying(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-600 via-yellow-500 to-red-600 relative overflow-hidden">
      {/* Ethiopian Pattern Overlay */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="h-full w-full" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 max-w-5xl">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-8 mb-8 border-4 border-yellow-400">
            <motion.h1 
              className="text-5xl md:text-7xl font-bold text-green-800 mb-4 cursor-pointer"
              animate={{ scale: [1, 1.02, 1] }}
              transition={{ duration: 3, repeat: Infinity }}
              onClick={playGreeting}
            >
              🌟 መልካም አዲስ ዓመት! 🌟
            </motion.h1>
            <h2 className="text-2xl md:text-4xl font-semibold text-red-700 mb-2">
              A New Year, A New Way to Reconnect With Amharic
            </h2>
            <p className="text-xl text-yellow-700 font-medium tracking-wide">From bkamharic.com</p>
          </div>
        </motion.div>

        {/* Hero Image Section */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-12"
        >
          <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white aspect-video md:aspect-[21/9]">
            <img 
              src="https://www.bkamharic.com/new-year/image/new-year-hero.jpg" 
              alt="Ethiopian New Year Celebration"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
            <div className="absolute bottom-8 left-8 text-white">
              <p className="text-3xl font-bold text-shadow">Reconnect. Relearn. Rediscover.</p>
            </div>
          </div>
        </motion.div>

        {/* Features Section */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {[
            { icon: FiUsers, title: "Interactive", desc: "Learn through conversations, quizzes, audio, and real‑life scenarios." },
            { icon: FiGlobe, title: "Self‑directed", desc: "Study at your own pace, anytime, anywhere." },
            { icon: FiHeart, title: "Culturally grounded", desc: "Lessons rooted in Ethiopian expressions, values, and lived experiences." },
            { icon: FiStar, title: "Tech‑powered", desc: "Smart feedback, adaptive learning, and tools that help you speak confidently." }
          ].map((feature, index) => (
            <motion.div 
              key={index}
              whileHover={{ scale: 1.03 }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 border-b-4 border-red-500 shadow-xl"
            >
              <div className="flex items-center mb-4">
                <div className="bg-green-100 p-3 rounded-xl mr-4">
                  <SafeIcon icon={feature.icon} className="text-3xl text-green-700" />
                </div>
                <h4 className="text-2xl font-bold text-green-800">{feature.title}</h4>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">{feature.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Mini Lesson Section */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-12"
        >
          <div className="bg-gradient-to-br from-green-700 to-green-900 rounded-3xl shadow-2xl p-8 md:p-12 text-white border-4 border-yellow-400">
            <div className="text-center mb-10">
              <h3 className="text-4xl md:text-5xl font-bold mb-4">🎁 A New Year Gift: Your First Mini‑Lesson</h3>
              <p className="text-xl text-green-100">Click the play button to hear the pronunciation</p>
            </div>

            <div className="grid md:grid-cols-2 gap-10">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10 relative">
                <h4 className="text-2xl font-bold mb-6 flex items-center">
                  <SafeIcon icon={FiBookOpen} className="mr-3 text-yellow-400" />
                  Vocabulary
                </h4>
                <AnimatePresence mode="wait">
                  <motion.div 
                    key={currentLesson}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="min-h-[180px] flex flex-col justify-center"
                  >
                    <div className="bg-white/10 rounded-2xl p-6 border border-white/10 text-center">
                      <div className="flex justify-center items-center gap-4 mb-4">
                        <p className="text-5xl font-bold text-yellow-300">{lessons[currentLesson].amharic}</p>
                        <button 
                          onClick={() => toggleAudio(lessons[currentLesson].audioUrl)}
                          className="bg-yellow-400 text-green-900 p-3 rounded-full hover:bg-yellow-300 transition-colors"
                        >
                          <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="text-xl" />
                        </button>
                      </div>
                      <p className="text-2xl text-white font-medium">{lessons[currentLesson].english}</p>
                      <p className="text-md text-green-300 mt-4 italic">Pronunciation: {lessons[currentLesson].pronunciation}</p>
                    </div>
                  </motion.div>
                </AnimatePresence>
                <button 
                  onClick={nextLesson}
                  className="mt-6 w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2"
                >
                  Next Word <SafeIcon icon={FiArrowRight} />
                </button>
              </div>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                <h4 className="text-2xl font-bold mb-6 flex items-center">
                  <SafeIcon icon={FiStar} className="mr-3 text-yellow-400" />
                  Quick Practice
                </h4>
                <div className="space-y-6">
                  <p className="text-lg">Try responding:</p>
                  <div className="bg-green-800/40 rounded-2xl p-6 border border-white/10">
                    <p className="text-2xl text-yellow-200"><strong>You:</strong> እንዴት ነህ?</p>
                    <p className="text-2xl text-white mt-4"><strong>Friend:</strong> ________</p>
                  </div>
                  <button 
                    onClick={() => {
                      setShowAnswer(!showAnswer);
                      if(!showAnswer) toggleAudio("https://www.bkamharic.com/new-year/audio/dehna-negn.mp3");
                    }}
                    className="w-full bg-yellow-400 hover:bg-yellow-500 text-green-900 font-bold py-4 rounded-xl transition-all duration-300"
                  >
                    {showAnswer ? 'Hide Answer' : 'Show Answer'}
                  </button>
                  <AnimatePresence>
                    {showAnswer && (
                      <motion.div 
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white/20 rounded-2xl p-6 text-center border border-white/20"
                      >
                        <p className="text-2xl text-yellow-200 font-bold mb-1">ደህና ነኝ፣ አመሰግናለሁ</p>
                        <p className="text-white text-lg italic">Dehna negn, amesegnalewu</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
            
            <audio ref={audioRef} onEnded={() => setIsPlaying(false)} />
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl p-10 md:p-16 border-4 border-yellow-400">
            <h3 className="text-4xl font-bold text-green-800 mb-8">🌱 Grow Your Amharic With Us</h3>
            <p className="text-2xl text-red-700 mb-10 font-bold italic">
              Join us in 2026 as we build a global community of Amharic learners who learn with joy, pride, and modern tools.
            </p>
            
            <div className="text-center space-y-6 mb-12">
              <div className="text-5xl font-black text-green-700">መልካም አዲስ ዓመት!</div>
              <div className="text-2xl text-red-600 font-bold uppercase tracking-widest">Reconnect. Relearn. Rediscover.</div>
            </div>

            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-green-600 to-red-600 text-white font-black text-2xl px-16 py-7 rounded-2xl shadow-2xl hover:shadow-green-500/20 transition-all duration-300 uppercase tracking-widest border-b-8 border-black/20"
              onClick={() => window.open('https://bkamharic.com', '_blank')}
            >
              Start Your Journey Today! 🚀
            </motion.button>
          </div>
        </motion.div>
      </div>
      
      {/* Footer Branding */}
      <div className="relative z-10 text-center py-8 text-white font-bold bg-black/20 backdrop-blur-md">
        © 2026 bkamharic.com
      </div>
    </div>
  );
}

export default App;